import React, { useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Check, ArrowLeft } from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import '../styles/pages/auth.css';

const VerifyPage = () => {
  const { verifyOtp, resendOtp } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const refs = useRef(Array.from({ length: 6 }, () => React.createRef()));

  if (!email) {
    return (
      <div className="auth-page">
        <div className="auth-page__grid-bg" />
        <div className="auth-page__glow auth-page__glow--1" />
        <div className="auth-page__glow auth-page__glow--2" />
        <div className="auth-page__container">
          <div className="auth-page__card auth-page__card--confirmation">
            <h1 className="auth-page__title">No email provided</h1>
            <p className="auth-page__desc">Please sign up first to receive a verification code.</p>
            <Link to="/signup" className="auth-page__submit" style={{ textDecoration: 'none', marginTop: 20 }}>
              Go to Sign Up
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (i, value) => {
    if (value && !/^\d+$/.test(value)) return;
    const next = [...code];
    next[i] = value;
    setCode(next);
    setError('');
    setSuccess('');
    if (value && i < 5) refs.current[i + 1].current.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      refs.current[i - 1].current.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain').trim();
    if (/^\d{6}$/.test(pasted)) {
      setCode(pasted.split(''));
      refs.current[5].current.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = code.join('');
    if (otpValue.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await verifyOtp(email, otpValue);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid verification code. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    setSuccess('');
    try {
      await resendOtp(email);
      setSuccess('A new code has been sent to your email');
      setCode(['', '', '', '', '', '']);
      refs.current[0].current.focus();
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__grid-bg" />
      <div className="auth-page__glow auth-page__glow--1" />
      <div className="auth-page__glow auth-page__glow--2" />

      <div className="auth-page__container">
        <Link to="/signup" className="auth-page__back">
          <ArrowLeft size={16} />
          Back to sign up
        </Link>

        <div className="auth-page__card">
          <div className="auth-page__header">
            <div className="verify-icon">
              <ShieldCheck size={40} />
            </div>
            <h1 className="auth-page__title">Verify your email</h1>
            <p className="auth-page__desc">
              Enter the 6-digit code sent to <strong>{email}</strong>
            </p>
          </div>

          <form className="auth-page__form" onSubmit={handleSubmit}>
            {error && (
              <div className="auth-page__error">
                <AlertTriangle size={15} />
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="verify-success">
                <Check size={15} />
                <span>{success}</span>
              </div>
            )}

            <div className="verify-otp">
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={refs.current[i]}
                  className={`verify-otp__input ${digit ? 'verify-otp__input--filled' : ''}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={i === 0 ? handlePaste : undefined}
                  disabled={loading}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <button type="submit" className="auth-page__submit" disabled={loading}>
              {loading ? (
                <><span className="spinner" /> Verifying...</>
              ) : (
                <><ShieldCheck size={18} /> Verify &amp; Continue</>
              )}
            </button>
          </form>

          <div className="auth-page__footer">
            Didn&rsquo;t receive a code?{' '}
            <button
              type="button"
              className="auth-page__link"
              onClick={handleResend}
              disabled={resending}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {resending ? 'Sending...' : 'Resend code'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;
